import { forwardRef, useImperativeHandle, useState, useEffect, useRef } from 'react';

import {
  Button,
  Card,
  Grid,
  List,
  CardHeader,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Divider,
  ListItem,
  Typography,
  Box,
  IconButton,
  InputBase,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DatePicker from '../Header/DatePicker';
import { i18n } from '../../../../language';
import { SearchIcon } from '../../../../components/Icon';
import { Dialog } from '../../../../components';
import { dutyAssignAPI } from '../../../../apis';

const useStyles = makeStyles((theme) => ({
  search: {
    borderRadius: '0.5rem',
    backgroundColor: theme.palette.font.white,
    width: '9.813rem',
    height: '2.55rem',
    display: 'flex',
    justifyContent: 'space-between',
    marginRight: '1.2rem',
    border: '0.0625rem solid #ccc',
  },
  input: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.919rem',
    letterSpacing: '0',
    color: theme.palette.font.sub,
    fontWeight: '500',
    margin: '0 0.938rem 0 0.938rem',
  },
  iconbtn: {
    padding: '0',
  },
  icon: {
    backgroundColor: '#4c93ff',
    color: theme.palette.font.white,
    padding: '0.5rem 0.5rem 0.438rem 0.438rem',
    borderRadius: '0.4875rem',
    fontSize: '1.875rem',
    margin: '0.313rem 0.313rem 0.313rem 0',
  },
  dialogTitle: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: theme.palette.font.sub,
    fontWeight: '500',
    marginBottom: '0.625rem',
  },
  checkBox: {
    '& .MuiCheckbox-root': {
      color: '#c0c6cb',
    },
    '& .MuiTypography-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      fontWeight: '500',
      color: '#2e4469',
    },
    '& .Mui-checked': {
      color: '#28d2ab',
    },
  },
}));

function not(all, part) {
  return all.filter((value) => !part.includes(value));
}

function intersection(a, b) {
  return a.filter((value) => b.includes(value));
}

function union(a, b) {
  return [...a, ...not(b, a)];
}

function formatDate(date) {
  const ds = `${date.getFullYear().toString().padStart(4, "0")}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
  return ds;
}

function formatSrsStaffKey(staff) {
  return `${staff.corpId} ${staff.staff_name}`;
}

function formatAreaStaffKey(staff) {
  return `${staff.corpId} ${staff.name}`;
}

function srsMapAreaStaff(staff) {
  const ret = {
    corpId: staff.corpId,
    rankCode: staff.rank_code,
    rankOrder: staff.rank_order,
    name: staff.staff_name,
    role: staff.role,
  };
  return ret;
}

const AreaStaffSelecter = (props, ref) => {
  const classes = useStyles();
  const [baseSelectedKeys, baseSelectedKeysSet] = useState([]);
  const [left, setLeft] = useState([]);
  const [right, setRight] = useState([]);
  const [open, openSet] = useState(false);
  const [dialogTitle, dialogTitleSet] = useState('');
  const [selectedDate, selectedDateSet] = useState(new Date());
  const [keyword, keywordSet] = useState('');
  const [leftChecked, leftCheckedSet] = useState([]);
  const [rightChecked, rightCheckedSet] = useState([]);
  const onConfirm = useRef();
  const staffMap = useRef(new Map());

  const handleToggleLeft = (value) => () => {
    const currentIndex = leftChecked.indexOf(value);
    const newChecked = [...leftChecked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    leftCheckedSet(newChecked);
  };

  const handleToggleRight = (value) => () => {
    const currentIndex = rightChecked.indexOf(value);
    const newChecked = [...rightChecked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    rightCheckedSet(newChecked);
  };

  const numberOfCheckedLeft = (items) => intersection(leftChecked, items).length;
  const numberOfCheckedRight = (items) => intersection(rightChecked, items).length;

  const handleToggleAllLeft = (items) => () => {
    if (numberOfCheckedLeft(items) === items.length) {
      leftCheckedSet(not(leftChecked, items));
    } else {
      leftCheckedSet(union(leftChecked, items));
    }
  };

  const handleToggleAllRight = (items) => () => {
    if (numberOfCheckedRight(items) === items.length) {
      rightCheckedSet(not(rightChecked, items));
    } else {
      rightCheckedSet(union(rightChecked, items));
    }
  };

  const handleCheckedRight = () => {
    const rightSet = new Set(right.concat(leftChecked));
    setRight(Array.from(rightSet));
    leftCheckedSet([]);
  };

  const handleCheckedLeft = () => {
    setRight(not(right, rightChecked));
    rightCheckedSet([]);
  };

  useImperativeHandle(ref, () => ({
    init: () => {
      keywordSet('');
      selectedDateSet(new Date());
      leftCheckedSet([]);
      rightCheckedSet([]);
      baseSelectedKeysSet([]);
    },
    setSelectedItems: (items) => {
      let selectedKeys = [];
      items.forEach((staff) => {
        const key0 = formatAreaStaffKey(staff);
        selectedKeys.push(key0);
        staffMap.current.set(key0, staff);
      });
      baseSelectedKeysSet(selectedKeys);
      setRight(selectedKeys);
    },
    setOnConfirm: (fn) => {
      onConfirm.current = fn;
    },
    open: () => {
      openSet(true);
    },
    close: () => {
      openSet(false);
    },
    setDialogTitle: (title) => {
      dialogTitleSet(title);
    },
  }));

  useEffect(() => {
    dutyAssignAPI
      .getDaStaffList({
        date: formatDate(selectedDate),
      })
      .then((res) => {
        const { result } = res;
        const srsStaffKeys = new Set(baseSelectedKeys);
        result.srsStaff.forEach((staff) => {
          const key0 = formatSrsStaffKey(staff);
          staffMap.current.set(key0, srsMapAreaStaff(staff));
          srsStaffKeys.add(key0);
        });
        setLeft(Array.from(srsStaffKeys));
      });
  }, [selectedDate]);

  const leftList = (title, items, keyword = '') => (
    <Card>
      <CardHeader
        sx={{ px: 2, py: 1 }}
        className={classes.checkBox}
        avatar={
          <Checkbox
            onClick={handleToggleAllLeft(items)}
            checked={numberOfCheckedLeft(items) === items.length && items.length !== 0}
            indeterminate={numberOfCheckedLeft(items) !== items.length && numberOfCheckedLeft(items) !== 0}
            disabled={items.length === 0}
            inputProps={{
              'aria-label': 'all items selected',
            }}
          />
        }
        title={title}
        subheader={`${numberOfCheckedLeft(items)}/${items.length} ${i18n.t(
          'DUTYASSIGN_ROSTER_TEMPLATE_STAFF_CHECKED'
        )}`}
      />
      <Divider />
      <List
        sx={{
          width: 200,
          bgcolor: 'background.paper',
          overflow: 'auto',
        }}
        style={{ height: 400, overflowY: 'scroll' }}
        dense
        component="div"
        role="list"
      >
        {items
          .filter((value) => keyword.trim().length <= 0 || value.toLowerCase().indexOf(keyword.toLowerCase()) >= 0)
          .map((value) => {
            const labelId = `transfer-list-all-item-${value}-label`;

            return (
              <ListItem key={value} role="listitem" onClick={handleToggleLeft(value)} className={classes.checkBox}>
                <ListItemIcon>
                  <Checkbox
                    checked={leftChecked.includes(value)}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{
                      'aria-labelledby': labelId,
                    }}
                  />
                </ListItemIcon>
                <ListItemText id={labelId} primary={`${staffMap.current.get(value).name}`} />
              </ListItem>
            );
          })}
      </List>
    </Card>
  );

  const rightList = (title, items, keyword = '') => (
    <Card>
      <CardHeader
        sx={{ px: 2, py: 1 }}
        className={classes.checkBox}
        avatar={
          <Checkbox
            onClick={handleToggleAllRight(items)}
            checked={numberOfCheckedRight(items) === items.length && items.length !== 0}
            indeterminate={numberOfCheckedRight(items) !== items.length && numberOfCheckedRight(items) !== 0}
            disabled={items.length === 0}
            inputProps={{
              'aria-label': 'all items selected',
            }}
          />
        }
        title={title}
        subheader={`${numberOfCheckedRight(items)}/${items.length} ${i18n.t(
          'DUTYASSIGN_ROSTER_TEMPLATE_STAFF_CHECKED'
        )}`}
      />
      <Divider />
      <List
        sx={{
          width: 200,
          bgcolor: 'background.paper',
          overflow: 'auto',
        }}
        style={{ height: 400, overflowY: 'scroll' }}
        dense
        component="div"
        role="list"
      >
        {items
          .filter((value) => keyword.trim().length <= 0 || value.toLowerCase().indexOf(keyword.toLowerCase()) >= 0)
          .map((value) => {
            const labelId = `transfer-list-all-item-${value}-label`;

            return (
              <ListItem key={value} role="listitem" onClick={handleToggleRight(value)} className={classes.checkBox}>
                <ListItemIcon>
                  <Checkbox
                    checked={rightChecked.includes(value)}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{
                      'aria-labelledby': labelId,
                    }}
                  />
                </ListItemIcon>
                <ListItemText id={labelId} primary={`${staffMap.current.get(value).name}`} />
              </ListItem>
            );
          })}
      </List>
    </Card>
  );

  return (
    <Dialog
      title={dialogTitle}
      cancel={i18n.t('DUTYASSIGN_CANCEL')}
      confirm={i18n.t('DUTYASSIGN_CONFIRM')}
      open={open}
      id="DutyAssign-Dialog-agx"
      handleOnClose={() => {
        return;
      }}
      handleClose={() => {
        openSet(false);
      }}
      handleCancel={() => {
        openSet(false);
      }}
      handleConfirm={() => {
        const selectedItems = right.map((key) => staffMap.current.get(key));
        onConfirm.current(selectedItems);
      }}
      width={900}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          marginTop: '0.8em',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <Typography style={{ marginRight: '1rem' }}>{i18n.t('DUTYASSIGN_FILTERBY')}</Typography>
          <DatePicker
            handleSelectDate={(e) => {
              selectedDateSet(e._d);
            }}
            id="DutyAssign-DatePicker-adp"
            selectDate={selectedDate}
          />
        </div>
        <Box className={classes.search}>
          <InputBase
            className={classes.input}
            value={keyword}
            onChange={(event) => {
              keywordSet(event.target.value);
            }}
            id="DutyAssign-InputBase-acj"
            placeholder={i18n.t('DUTYASSIGN_ROSTER_SEARCH')}
          />
          <IconButton className={classes.iconbtn} id="DutyAssign-IconButton-ack">
            <SearchIcon className={classes.icon} />
          </IconButton>
        </Box>
      </div>

      <Grid
        container
        spacing={2}
        sx={{ justifyContent: 'center', alignItems: 'center' }}
        style={{ marginTop: '0.8em' }}
      >
        <Grid sm={5} item>
          {leftList(i18n.t('DUTYASSIGN_ROSTER_TEMPLATE_STAFF_LIST'), left, keyword)}
        </Grid>
        <Grid sm={2} item style={{ display: 'flex', alignItems: 'center' }}>
          <Grid container direction="column" sx={{ alignItems: 'center' }}>
            <Button
              sx={{ my: 0.5 }}
              style={{
                marginBottom: '0.5rem',
                color: '#fff',
                backgroundColor: '#4c93ff',
                fontWeight: 'bold',
                fontSize: '1rem',
              }}
              variant="outlined"
              size="small"
              onClick={handleCheckedRight}
              disabled={leftChecked.length === 0}
              id="move selected right"
              aria-label="move selected right"
            >
              &gt;
            </Button>
            <Button
              sx={{ my: 0.5 }}
              style={{ color: '#fff', backgroundColor: '#4c93ff', fontSize: '1rem', fontWeight: 'bold' }}
              variant="outlined"
              size="small"
              onClick={handleCheckedLeft}
              disabled={rightChecked.length === 0}
              id="move selected left"
              aria-label="move selected left"
            >
              &lt;
            </Button>
          </Grid>
        </Grid>
        <Grid sm={5} item>
          {rightList(i18n.t('DUTYASSIGN_ROSTER_TEMPLATE_SELECTED_STAFF'), right)}
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default forwardRef(AreaStaffSelecter);
